import type { TenantSession } from '../../../utils/tenantSessionStorage';

export type IndicatorType = 'alarm' | 'imOk' | 'message';

/**
 * Single source of truth for which indicator wins when a session has more
 * than one. Both the cross-session pill picker and the per-session row icon
 * rely on this order.
 */
const INDICATOR_PRIORITY: readonly IndicatorType[] = ['alarm', 'imOk', 'message'] as const;

const HAS_INDICATOR: Record<IndicatorType, (s: TenantSession) => boolean> = {
  alarm: s => Boolean(s.hasActiveAlarm),
  imOk: s => Boolean(s.hasActiveImOk),
  message: s => Boolean(s.hasUnreadMessages),
};

/** The highest-priority indicator on this session, or null. */
export const getIndicatorType = (session: TenantSession): IndicatorType | null =>
  INDICATOR_PRIORITY.find(type => HAS_INDICATOR[type](session)) ?? null;

/**
 * Returns the sessions with any indicator, sorted by indicator priority —
 * sessions with an alarm come first, then imOk, then message. Sessions
 * without any indicator are excluded.
 */
export const findSessionsWithIndicator = (sessions: TenantSession[]): TenantSession[] => {
  const result: TenantSession[] = [];
  for (const type of INDICATOR_PRIORITY) {
    for (const session of sessions) {
      if (HAS_INDICATOR[type](session) && !result.includes(session)) result.push(session);
    }
  }
  return result;
};
